import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../../../../redux/store";
import { setBopChart } from "../../../../../../../../redux/diagnosestate/diagnoseAction";

const SITE_OPTIONS = [
  { value: "", label: "—" },
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },

  { value: "x", label: "x" },
] as const;

const SITE_COUNT = 6;

function normalizeSites(arr: string[] | undefined): string[] {
  if (!Array.isArray(arr)) return Array.from({ length: SITE_COUNT }, () => "");
  const copy = arr.slice(0, SITE_COUNT).map((v) => (v != null ? String(v) : ""));
  while (copy.length < SITE_COUNT) copy.push("");
  return copy;
}

function BopChart() {
  const dispatch = useDispatch();
  const sites = useSelector((state: RootState) =>
    normalizeSites(state.diagnose.bopChart)
  );

  const updateSite = useCallback(
    (index: number, value: string) => {
      const next = sites.map((v, i) => (i === index ? value : v));
      dispatch(setBopChart(next));
    },
    [dispatch, sites]
  );

  const stats = useMemo(() => {
    const filled = sites.filter((v) => v).length;
    
    return { total: SITE_COUNT, filled };
  }, [sites]);

  const clearAll = useCallback(() => {
    dispatch(setBopChart(Array.from({ length: SITE_COUNT }, () => "")));
  }, [dispatch]);

  const selectClass = (value: string) =>
    `w-full min-w-0 px-1.5 py-1.5 text-center text-sm rounded-md border bg-slate-50 dark:bg-slate-700/80 text-slate-800 dark:text-slate-200 transition-colors focus:outline-none focus:ring-0 focus:border-blue-800 dark:focus:border-blue-400 cursor-pointer appearance-none${
      value === "x"
        ? "border-amber-400/60 dark:border-amber-500/50 bg-amber-50/50 dark:bg-amber-900/20"
        : value
          ? "border-green-500/60 dark:border-green-500/50 bg-green-50/50 dark:bg-green-900/20"
          : "border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500 border-blue-800 "
    }`;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">
            BPE chart
          </h4>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Record per site (1–4 or x)</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="text-xs text-slate-600 dark:text-slate-300 tabular-nums">Filled: <strong>{stats.filled}</strong>/{stats.total}</span>
          <button
            type="button"
            onClick={clearAll}
            className="text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 underline focus:outline-none focus:ring-2 focus:ring-primary/30 rounded px-1"
          >
            Clear all
          </button>
        </div>
      </div>

      <div className="p-4">
        <div className="grid grid-cols-3 gap-2 max-w-[240px]">
          {sites.map((value, i) => (
            <div key={i}>
              <label
                htmlFor={`bop-site-${i}`}
                className="text-[10px] text-slate-400 dark:text-slate-500 sr-only"
              >
                Site {i + 1}
              </label>
              <select
                id={`bop-site-${i}`}
                value={value}
                onChange={(e) => updateSite(i, e.target.value)}
                className={selectClass(value)}
                aria-label={`Site ${i + 1}`}
              >
                {SITE_OPTIONS.map((opt) => (
                  <option key={opt.value || "empty"} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-4 text-[10px] text-slate-500 dark:text-slate-400">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700" />
            Empty
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded border border-green-500/60 bg-green-50/50 dark:bg-green-900/20" />
            1–4 
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded border border-amber-400/60 bg-amber-50/50 dark:bg-amber-900/20" />
            x
          </span>
        </div>
      </div>
    </div>
  );
}

export default BopChart;
